import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import LocalNotificationService, { REMINDER_OPTIONS, ActivityReminder as ReminderType } from '../services/localNotifications';
import ApiService from '../services/api';

type ReminderTarget = 'start' | 'end' | 'both';

interface ActivityReminderProps {
  activityUuid: string;
  activityName: string;
  startDate: string;
  startTime: string;
  endTime?: string;
  childName?: string;
  location?: string;
  responsibleParentUuid?: string; // The designated parent for this activity
  currentUserUuid?: string; // The currently logged in user's UUID
  responsibleParentName?: string; // Name of the designated parent for display
  reminderMinutes?: number | null; // Reminder minutes from activity (stored in backend)
  reminderTarget?: ReminderTarget | null; // Which time to remind for (start, end, both)
  onReminderChange?: (minutes: number | null, target: ReminderTarget | null) => void; // Callback when reminder changes
}

const REMINDER_TARGET_OPTIONS: { label: string; value: ReminderTarget }[] = [
  { label: 'Start Time', value: 'start' },
  { label: 'End Time', value: 'end' },
  { label: 'Both', value: 'both' },
];

const ActivityReminderComponent: React.FC<ActivityReminderProps> = ({
  activityUuid,
  activityName,
  startDate,
  startTime,
  endTime,
  childName,
  location,
  responsibleParentUuid,
  currentUserUuid,
  responsibleParentName,
  reminderMinutes,
  reminderTarget: reminderTargetProp,
  onReminderChange
}) => {
  const [localReminder, setLocalReminder] = useState<ReminderType | null>(null);
  const [selectedMinutes, setSelectedMinutes] = useState<number | null>(reminderMinutes ?? null);
  const [selectedTarget, setSelectedTarget] = useState<ReminderTarget | null>(reminderTargetProp ?? null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const hasAutoScheduled = useRef(false);

  // Check native platform immediately, not in useEffect
  const isNative = Capacitor.isNativePlatform();

  const apiService = ApiService.getInstance();
  const localNotificationService = LocalNotificationService.getInstance();

  // Determine if current user should receive reminders
  const shouldReceiveReminder = !responsibleParentUuid || responsibleParentUuid === currentUserUuid;
  const hasDesignatedParent = !!responsibleParentUuid;

  // Auto-schedule local alarm when activity has reminder_minutes set and user should receive it
  useEffect(() => {
    if (!isNative || !activityUuid) return;

    const autoScheduleReminder = async () => {
      if (reminderMinutes !== null && reminderMinutes !== undefined && shouldReceiveReminder) {
        const existingReminder = await localNotificationService.getReminder(activityUuid);
        const effectiveTarget = reminderTargetProp || 'start';

        if (!existingReminder || existingReminder.minutesBefore !== reminderMinutes || existingReminder.reminderTarget !== effectiveTarget || !existingReminder.useAlarmKit) {
          console.log(`Auto-scheduling reminder for activity ${activityUuid}: ${reminderMinutes} minutes before (${effectiveTarget})`);

          await localNotificationService.cancelReminder(activityUuid);

          const reminder = await localNotificationService.scheduleReminder({
            activityUuid,
            activityName,
            startDate,
            startTime,
            endTime,
            minutesBefore: reminderMinutes,
            reminderTarget: effectiveTarget,
            childName,
            location
          });

          if (reminder) {
            setLocalReminder(reminder);
          }
        } else {
          setLocalReminder(existingReminder);
        }
        setSelectedMinutes(reminderMinutes);
        setSelectedTarget(reminderTargetProp || 'start');
      } else if (reminderMinutes === null || reminderMinutes === undefined) {
        const existingReminder = await localNotificationService.getReminder(activityUuid);
        if (existingReminder) {
          await localNotificationService.cancelReminder(activityUuid);
          setLocalReminder(null);
        }
        setSelectedMinutes(null);
      } else if (!shouldReceiveReminder) {
        const existingReminder = await localNotificationService.getReminder(activityUuid);
        if (existingReminder) {
          await localNotificationService.cancelReminder(activityUuid);
          setLocalReminder(null);
        }
      }
    };

    if (!hasAutoScheduled.current) {
      hasAutoScheduled.current = true;
      autoScheduleReminder();
    }
  }, [activityUuid, reminderMinutes, reminderTargetProp, shouldReceiveReminder, isNative]);

  // Update selectedMinutes when reminderMinutes prop changes
  useEffect(() => {
    setSelectedMinutes(reminderMinutes ?? null);
  }, [reminderMinutes]);

  useEffect(() => {
    setSelectedTarget(reminderTargetProp ?? null);
  }, [reminderTargetProp]);

  const handleSetReminder = async () => {
    if (selectedMinutes === null || selectedTarget === null) return;

    setIsLoading(true);
    try {
      const response = await apiService.configureActivity(activityUuid, {
        reminder_minutes: selectedMinutes,
        reminder_target: selectedTarget
      });

      if (response.success) {
        if (isNative && shouldReceiveReminder) {
          await localNotificationService.cancelReminder(activityUuid);

          const reminder = await localNotificationService.scheduleReminder({
            activityUuid,
            activityName,
            startDate,
            startTime,
            endTime,
            minutesBefore: selectedMinutes,
            reminderTarget: selectedTarget,
            childName,
            location
          });

          if (reminder) {
            setLocalReminder(reminder);
          }
        }

        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);

        onReminderChange?.(selectedMinutes, selectedTarget);
      } else {
        console.error('Failed to save reminder to server:', response.error);
      }
    } catch (error: any) {
      console.error('Failed to set reminder:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelReminder = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.configureActivity(activityUuid, {
        reminder_minutes: null,
        reminder_target: null
      });

      if (response.success) {
        if (isNative) {
          await localNotificationService.cancelReminder(activityUuid);
          setLocalReminder(null);
        }
        setSelectedMinutes(null);
        setSelectedTarget(null);

        onReminderChange?.(null, null);
      } else {
        console.error('Failed to clear reminder from server:', response.error);
      }
    } catch (error) {
      console.error('Failed to cancel reminder:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if the activity is in the past
  const isActivityPast = () => {
    try {
      const datePart = startDate.split('T')[0];
      const [year, month, day] = datePart.split('-').map(n => parseInt(n, 10));
      let hours = 0;
      let minutes = 0;
      if (startTime) {
        const timeParts = startTime.split(':');
        hours = parseInt(timeParts[0], 10);
        minutes = parseInt(timeParts[1], 10);
      }
      const activityDate = new Date(year, month - 1, day, hours, minutes);
      return activityDate <= new Date();
    } catch {
      return false;
    }
  };

  // Don't show if activity is in the past
  if (isActivityPast()) {
    return null;
  }

  const getReminderLabel = (minutes: number): string => {
    const option = REMINDER_OPTIONS.find(o => o.value === minutes);
    return option?.label || `${minutes} minutes before`;
  };

  const getTargetLabel = (target: ReminderTarget): string => {
    const option = REMINDER_TARGET_OPTIONS.find(o => o.value === target);
    return option?.label || target;
  };

  // Check if reminder is currently active
  const hasActiveReminder = reminderMinutes !== null && reminderMinutes !== undefined;
  const activeTarget = reminderTargetProp || 'start';

  // Can set reminder only when both time and target are selected
  const canSetReminder = selectedMinutes !== null && selectedTarget !== null;

  // If there's a designated parent and current user is not them, show info message
  if (hasDesignatedParent && !shouldReceiveReminder) {
    return (
      <div style={{
        marginTop: '24px',
        padding: '16px',
        background: '#f0f4f8',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <label style={{
          fontSize: '16px',
          color: '#2d3748',
          marginBottom: '8px',
          display: 'block',
          fontWeight: '600'
        }}>
          {'\u23F0'} Reminder
        </label>
        <p style={{ fontSize: '13px', color: '#666' }}>
          {hasActiveReminder
            ? `Reminder set: ${getReminderLabel(reminderMinutes!)} (${getTargetLabel(activeTarget)}). ${responsibleParentName || 'The designated parent'} will receive this reminder.`
            : `${responsibleParentName || 'The designated parent'} is responsible for this activity and will receive any reminder alerts.`}
        </p>
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '24px',
      padding: '16px',
      background: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    }}>
      <label style={{
        fontSize: '16px',
        color: '#2d3748',
        marginBottom: '8px',
        display: 'block',
        fontWeight: '600'
      }}>
        {'\u23F0'} Set Reminder
      </label>
      <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
        {hasDesignatedParent
          ? `You are the designated parent for this activity. Only you will receive the alarm reminder.${!isNative ? ' Alarms will trigger on the mobile app.' : ''}`
          : `No designated parent is set, so both parents will receive the alarm reminder.${!isNative ? ' Alarms will trigger on the mobile app.' : ' To send reminders to only one parent, set a designated parent in the activity settings.'}`}
      </p>

      {hasActiveReminder ? (
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px',
            background: 'linear-gradient(135deg, #48bb78, #38a169)',
            borderRadius: '8px',
            marginBottom: '12px'
          }}>
            <span style={{ color: 'white', fontWeight: '600' }}>
              {'\u2705'} Reminder set: {getReminderLabel(reminderMinutes!)} ({getTargetLabel(activeTarget)})
            </span>
          </div>
          <button
            onClick={handleCancelReminder}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#dc3545',
              background: 'white',
              border: '2px solid #dc3545',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1
            }}
          >
            {isLoading ? 'Cancelling...' : 'Cancel Reminder'}
          </button>
        </div>
      ) : (
        <div>
          {/* Reminder Target Selection - Mandatory */}
          <label style={{
            fontSize: '13px',
            color: '#4a5568',
            marginBottom: '6px',
            display: 'block',
            fontWeight: '600'
          }}>
            Remind me for:
          </label>
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '12px'
          }}>
            {REMINDER_TARGET_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedTarget(option.value)}
                style={{
                  flex: 1,
                  padding: '10px 8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: selectedTarget === option.value ? 'white' : '#4a5568',
                  background: selectedTarget === option.value
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'white',
                  border: selectedTarget === option.value
                    ? '2px solid #667eea'
                    : '2px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Reminder Time Selection */}
          <select
            value={selectedMinutes ?? ''}
            onChange={(e) => setSelectedMinutes(e.target.value ? parseInt(e.target.value, 10) : null)}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer',
              marginBottom: '12px'
            }}
          >
            <option value="">Select reminder time...</option>
            {REMINDER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Validation message */}
          {selectedMinutes !== null && selectedTarget === null && (
            <p style={{ fontSize: '12px', color: '#e53e3e', marginBottom: '8px' }}>
              Please select whether this reminder is for the start time, end time, or both.
            </p>
          )}

          <button
            onClick={handleSetReminder}
            disabled={isLoading || !canSetReminder}
            style={{
              width: '100%',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '600',
              color: 'white',
              background: !canSetReminder
                ? '#a0aec0'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '6px',
              cursor: (isLoading || !canSetReminder) ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              transition: 'all 0.3s ease',
              boxShadow: canSetReminder ? '0 2px 8px rgba(102, 126, 234, 0.3)' : 'none'
            }}
          >
            {isLoading ? 'Setting...' : 'Set Reminder'}
          </button>

          {showSuccess && (
            <div style={{
              marginTop: '12px',
              padding: '10px',
              background: '#d4edda',
              color: '#155724',
              borderRadius: '6px',
              textAlign: 'center',
              fontWeight: '600'
            }}>
              {'\u2705'} Reminder set successfully!
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityReminderComponent;
